import { useMemo, useState } from 'react'
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { CloseIcon, InfoIcon } from 'components/Icons'
import Gauge from '../StatCards/Gauge'
import { MIN_CARD_HEIGHT } from '../cardHeight'

const SEVERITY = [
  { atLeast: 15, color: 'error' },
  { atLeast: 5, color: 'warning' },
  { atLeast: 0, color: 'success' },
]

const severityOf = percent => SEVERITY.find(level => (percent ?? 0) >= level.atLeast) ?? SEVERITY[SEVERITY.length - 1]

const MAX_NAMES = 12

const TILE_GRID = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1 }

const Members = ({ label, members }) => {
  const { t } = useTranslation()
  const shown = members.slice(0, MAX_NAMES)
  const extra = members.length - shown.length

  return (
    <Box sx={{ py: 0.5, maxWidth: 260 }}>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.75, mb: 0.5 }}>
        {label}
      </Typography>
      {shown.map((member, index) => (
        <Typography key={`${member.name}-${index}`} variant="body2" sx={{ lineHeight: 1.5 }}>
          {member.name}
        </Typography>
      ))}
      {extra > 0 && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.75 }}>
          {t('and {{count}} more', { count: extra })}
        </Typography>
      )}
      <Typography variant="caption" sx={{ display: 'block', mt: 0.75, opacity: 0.9, fontStyle: 'italic' }}>
        {t('Click for details')}
      </Typography>
    </Box>
  )
}


const FIELD_LABELS = {
  grade: 'Grade',
  absences: 'Absences',
  rate: 'Absence Rate',
  streak: 'Longest Streak',
  lastDay: 'Last Absence',
}

const columnsFrom = members => {
  const keys = []

  for (const member of members) {
    for (const key of Object.keys(member ?? {})) {
      if (key !== 'name' && !keys.includes(key)) keys.push(key)
    }
  }

  return keys
}

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (key === 'rate') return `${value}%`

  return String(value)
}

const MemberDialog = ({ open, onClose, label, members }) => {
  const { t } = useTranslation()
  const columns = useMemo(() => columnsFrom(members), [members])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {t(label)}
        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {t('{{count}} students', { count: members.length })}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>{t('Student')}</TableCell>
              {columns.map(key => (
                <TableCell key={key} align="right" sx={{ fontWeight: 700 }}>
                  {t(FIELD_LABELS[key] ?? key)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((member, index) => (
              <TableRow key={`${member.name}-${index}`} hover>
                <TableCell>{member.name}</TableCell>
                {columns.map(key => (
                  <TableCell key={key} align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatValue(key, member[key])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  )
}

const Cohort = ({ cohort }) => {
  const { t } = useTranslation()
  const { label, description, count, total, percent, members = [] } = cohort
  const [open, setOpen] = useState(false)

  const measurable = total != null && Number(total) > 0 && percent != null
  const severity = severityOf(percent)
  const hasMembers = members.length > 0

  const tile = (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={0.5}
      tabIndex={hasMembers ? 0 : -1}
      role={hasMembers ? 'button' : undefined}
      onClick={hasMembers ? () => setOpen(true) : undefined}
      onKeyDown={hasMembers ? event => ['Enter', ' '].includes(event.key) && (event.preventDefault(), setOpen(true)) : undefined}
      sx={theme => ({
        height: '100%',
        minWidth: 0,
        px: 1,
        py: 1.5,
        borderRadius: 2,
        outline: 'none',
        cursor: hasMembers ? 'pointer' : 'default',
        transition: 'background-color .15s',
        '&:hover, &:focus-visible': hasMembers
          ? { bgcolor: alpha(theme.palette[severity.color].main, theme.palette.mode === 'dark' ? 0.16 : 0.07) }
          : undefined,
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ maxWidth: '100%' }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap title={t(label)}>
          {t(label)}
        </Typography>
        {description && (
          <Tooltip title={t(description)}>
            <InfoIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
          </Tooltip>
        )}
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {t('{{count}} students', { count: Number(count ?? members.length ?? 0) })}
      </Typography>

      {measurable ? (
        <Gauge
          value={percent}
          max={100}
          unit="%"
          display={Number(percent).toFixed(1)}
          color={`var(--cohort-${severity.color})`}
          size={1.1}
        />
      ) : (
        <Chip
          label={count ?? members.length}
          color={severity.color}
          variant="outlined"
          sx={{ mt: 1, fontWeight: 700, fontSize: 24, height: 56, minWidth: 84, borderWidth: 2, borderRadius: 2 }}
        />
      )}
    </Stack>
  )

  if (!hasMembers) return tile

  return (
    <>
      <Tooltip
        arrow
        placement="bottom"
        enterTouchDelay={0}
        title={<Members label={t('{{label}} — {{count}} students', { label: t(label), count: members.length })} members={members} />}
      >
        {tile}
      </Tooltip>
      <MemberDialog open={open} onClose={() => setOpen(false)} label={label} members={members} />
    </>
  )
}

export default function CohortGauges({
  title,
  summaryLabel,
  summaryTotal,
  cohorts = [],
  isReady = true,
  emptyMessage = 'No data to report.',
  minHeight = MIN_CARD_HEIGHT,
}) {
  const { t } = useTranslation()

  return (
    <Paper
      elevation={0}
      sx={theme => ({
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '--cohort-error': theme.palette.error.main,
        '--cohort-warning': theme.palette.warning.main,
        '--cohort-success': theme.palette.success.main,
      })}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} noWrap>
          {title}
        </Typography>
        {isReady && summaryLabel && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {t(summaryLabel)}
            {summaryTotal != null && ` · ${t('{{count}} students', { count: Number(summaryTotal) })}`}
          </Typography>
        )}
      </Stack>

      {!isReady ? (
        <Box sx={TILE_GRID}>
          {[0, 1, 2].map(index => (
            <Skeleton key={index} variant="rounded" height={140} />
          ))}
        </Box>
      ) : !cohorts.length ? (
        <Typography variant="body2" color="text.secondary">
          {t(emptyMessage)}
        </Typography>
      ) : (
        <Box sx={TILE_GRID}>
          {cohorts.map(cohort => (
            <Cohort key={cohort.label} cohort={cohort} />
          ))}
        </Box>
      )}
    </Paper>
  )
}
